export enum Mode {
  CREATE = 'create',
  EDIT = 'edit',
}

export enum FeatureTypes {
  DOMAIN = 'domain',
  FEATURE = 'feature',
  CAPABILITY = 'capability',
  RETENTION = 'retention',
  BACKUP = 'backup',
  SUPPORT = 'support',
  ONE_TIME_SERVICE = 'one_time_service',
}

export enum CloudProvider {
  GCP = 'gcp',
  MOCK_CLOUD = 'mock-cloud',
  UNSET = 'unset',
}

export enum ConfigurableStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum FeatureStatus {
  AVAILABLE = 'available',
  NOT_AVAILABLE = 'not_available',
}

export enum FlexPlan {
  FREE = 'free',
  PAID = 'paid',
}

export enum PaymentPeriod {
  MONTH = 'month',
  YEAR = 'year',
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
}

export enum PricingType {
  FREE = 'free',
}

export interface Pricing {
  multiplier: {
    $numberDecimal: string,
  },
  price: {
    amount: {
      $numberDecimal: string,
      currency: Currency,
    },
    type: PricingType,
    _id: string,
  },
}

export interface PlanFeature {
  refId: string,
  status: FeatureStatus,
  pricing: {
    [key: string]: Pricing,
  },
  order: number
}

export interface PlanBundle {
  refId: string,
  status: FeatureStatus,
  pricing: {
    [key: string]: Pricing,
  },
}

export interface Plan {
  _id: string,
  acceptedPaymentVendors: string[],
  alpha: boolean,
  baseFeatures: PlanFeature[],
  basePricing: {
    [key: string]: Pricing,
  },
  bundles?: PlanBundle[],
  code: string,
  createdAt: string,
  creator: string,
  current: boolean,
  features: PlanFeature[],
  meta: {
    [key: string]: any,
  },
  name: string,
  planGroup: string,
  priority: number,
  providers: PlanFeature[],
  regions: PlanFeature[],
  sizes: PlanFeature[],
  status: ConfigurableStatus,
  updatedAt: string,
}

export interface ConfigurableSize {
  _id: string,
  vendors: {
    [key: string]: {
      [key: string]: string,
    }
  },
  size: {
    value: number,
    unit: string
  },
  provider: string,
  status: ConfigurableStatus,
  name: string,
  meta: {
    [key: string]: any,
  },
  description: string,
  defaultPricing: {
    [key: string]: Pricing,
  },
  creator: string,
  createdAt: string,
  code: string,
  alpha: boolean,
}

export interface ConfigurableRegion {
  _id: string,
  alpha: boolean,
  code: string,
  createdAt: string,
  creator: string,
  defaultPricing: {
    [key: string]: Pricing,
  },
  description: string,
  meta: {
    [key: string]: any,
  },
  name: string,
  order: number,
  provider: string,
  status: ConfigurableStatus,
  vendors: {
    [key: string]: {
      [key: string]: string,
    }
  },
}

export interface ConfigurableProvider {
  _id: string,
  vendors: {
    [key: string]: {
      [key: string]: string,
    }
  },
  status: ConfigurableStatus,
  name: string,
  meta: {
    [key: string]: any,
  },
  defaultPricing: {
    [key: string]: Pricing,
  },
  creator: string,
  createdAt: string,
  code: string,
  alpha: boolean,
}

export interface ConfigurableFeature {
  _id: string,
  vendors: {
    [key: string]: {
      [key: string]: string,
    }
  },
  type: FeatureTypes,
  status: ConfigurableStatus,
  order: number,
  name: string,
  meta: {
    [key: string]: any,
  },
  description: string,
  defaultPricing: {
    [key: string]: Pricing,
  },
  creator: string,
  createdAt: string,
  code: string,
  availableFor: string[],
  alpha: boolean,
  actions: {
    [key: string]: string[],
  },
}

export interface ServerCreationState {
  name: string,
  inProgress?: boolean,
  subscription: {
    _id: string,
    planCode: string,
    planConfig: {
      region: string,
      features: string[],
      bundles?: string[],
      size: string,
      provider: CloudProvider,
    },
    specialFeatures: {
      domain: string,
      backup: string,
      retention: string,
    },
    planId: string,
    providerId: string,
  }
  activePaymentPeriod?: PaymentPeriod,
  pricing?: {
    total: number,
    currency: string,
    isDiscountApplied: false,
    discountCode: '',
    discountedTotal: {
      subtotal: 0,
      tax: 0,
      total: 0,
      discount: 0,
      credit: 0,
      balance: 0,
      currency: 'USD',
    },
  },
  isLoading?: boolean,
  provisionSummary?: {
    dataToVendor: {
      company: string,
      deployment: string,
      paymentMethod: string,
      subscription: string
    },
    items: {
      name: string,
      description: string,
      quantity: number,
      meta: {
        basePrice: boolean
      },
      pricing: any,
    }[],
    total: {
      amount: string,
      currency: string,
    }
  },
  isActiveServerFree: boolean,
  serverId?: string,
  supportLevel?: string,
  oneTimeServices?: string[],
}

export interface ConfigurableBundle {
  _id: string,
  code: string,
  name: string,
  description?: string,
  features: string[],
  meta?: { catalogTitle?: string },
}

export interface ServerConfigurables {
  [key: string]: {
    regions: ConfigurableRegion[],
    sizes: ConfigurableSize[],
    features: ConfigurableFeature[],
    providers: ConfigurableProvider[],
    bundles?: ConfigurableBundle[],
  }
}

export enum PaymentVendor {
  FREE = 'free',
  PADDLE = 'paddle',
}

export interface ServerCreationPayload {
  name: string;
  paymentCycle: PaymentPeriod;
  planConfig: {
    provider?: string;
    region: string;
    size: string;
    features: string[];
    bundles?: string[];
  };
  company?: string;
  project?: string;
  provider?: string;
  plan: string;
  planCode?: string;
  payment: {
    vendor: PaymentVendor;
  };
}
